import { axiosClient as axios } from './axiosClient';

import { api } from '@config/apiEndpoints';
import {
  failureResult,
  ResultViewModel,
  successResult,
} from '@models/common/ResultViewModel';
import {
  SalesTaxListResultViewModel,
  SalesTaxResultViewModel,
  SalesTaxSelectOptionResultViewModel,
  SalesTaxTableResultViewModel,
  SalesTaxViewModel,
} from '@models/SalesTax';
import { SalesTaxRateListResultViewModel } from '@models/SalesTaxRate';
import { handleAxiosError } from '@utils/axiosErrorHandler';
import { getDataUrl } from '@utils/dataUrls';

export const SalesTaxService = {
  create: async function (
    tax: SalesTaxViewModel
  ): Promise<SalesTaxResultViewModel | ResultViewModel> {
    const createUrl = `${api.BASE_URL}${api.SALESTAXES_ENDPOINT}`;

    try {
      const response = await axios.post(createUrl, tax);
      return response.data as SalesTaxResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getList: async function (
    page?: number,
    pageSize?: number,
    searchFields?: string,
    search?: string,
    filters?: string,
    sortBy?: string,
    sortDirection?: string
  ): Promise<SalesTaxTableResultViewModel | ResultViewModel> {
    let dataUrl = getDataUrl(
      api.BASE_URL,
      api.SALESTAXES_ENDPOINT,
      page,
      pageSize,
      searchFields,
      search,
      sortBy,
      sortDirection
    );

    if (filters) {
      dataUrl += filters;
    }

    try {
      const response = await axios.get(dataUrl);
      return response.data as SalesTaxTableResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getById: async function (
    id: number
  ): Promise<SalesTaxResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.SALESTAXES_ENDPOINT}/${id}`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as SalesTaxResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getSalesTaxSelectOptions: async function (): Promise<
    SalesTaxSelectOptionResultViewModel | ResultViewModel
  > {
    const dataUrl = `${api.BASE_URL}${api.SALESTAXES_ENDPOINT}/select-options`;
    try {
      const response = await axios.get(dataUrl);
      return response.data as SalesTaxSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getSalesTaxRates: async function (
    id: number
  ): Promise<SalesTaxRateListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.SALESTAXES_ENDPOINT}/${id}/sales-tax-rates`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as SalesTaxRateListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  getSalesTaxEffectiveRates: async function (
    effectiveDate: Date
  ): Promise<SalesTaxListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${
      api.SALESTAXES_ENDPOINT
    }/effective-rates?effectiveDate=${effectiveDate.toISOString()}`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as SalesTaxListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  update: async function (
    id: number,
    tax: SalesTaxViewModel
  ): Promise<ResultViewModel> {
    const updateUrl = `${api.BASE_URL}${api.SALESTAXES_ENDPOINT}/${id}`;

    try {
      const response = await axios.put(updateUrl, tax);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  delete: async function (id: number): Promise<ResultViewModel> {
    const deleteURL = `${api.BASE_URL}${api.SALESTAXES_ENDPOINT}/${id}`;

    try {
      const response = await axios.delete(deleteURL);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
};
